// The P&Per's live speed (Fernando, 2026-09-28): a chip in the picking and packing headers
// showing the EXPECTED orders/hour of this round — the part done at the real pace, the rest
// at the person's previous clean round's pace — with a racing-sim delta against that
// previous round (▲ green = this round is on course to beat it, ▼ red = slower, ▬ level).
// The number itself is coloured like the statistics: ≥30 green, ≥20 orange, else red.
// Tap → a sheet with the parts. One component, both screens; the model is
// backend/pp_tracking.round_delta, polled — nothing is computed here.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { fmtDur } from '../statistics/shared';

export interface Delta {
  round_id: number | null;
  status?: string;
  stage?: 'before_picking' | 'picking' | 'changeover' | 'packing' | 'done';
  person?: string | null;
  date?: string | null;
  orders?: number; live?: number; packed?: number; processed?: number;
  plan_stops?: number | null; stops_done?: number; stops_left?: number | null;
  elapsed_min?: number; remaining_min?: number;
  picking_min?: number | null; picking_final?: boolean;
  changeover_min?: number | null;
  packing_min?: number | null; packing_final?: boolean;
  sec_per_stop?: number | null; sec_per_order?: number | null;
  expected_oph?: number | null;
  delta?: number | null; delta_sign?: 'up' | 'down' | 'flat' | null;
  plan_metres?: number | null; metres_left?: number | null; metres_scale?: number | null;
  swaps_done?: number; swaps_planned?: number | null; swap_min?: number | null;
  pace_source?: 'own' | 'warehouse' | null;
  pace_parts?: { walk_min: number; shelf_min: number; box_min: number; pieces_left: number } | null;
  picker?: string | null; packers?: string | null;
  ref?: { round_id: number; date: string | null; oph: number; orders: number; sec_per_stop: number;
          sec_per_order: number; picking_min: number; packing_min: number; metres?: number | null;
          swaps?: number; sec_per_swap?: number | null } | null;
  names?: Record<string, string>;
}

type Theme = 'light' | 'dark';

/** Speed colour, statistics thresholds; the light (tablet) theme needs darker inks. */
export function speedCls(oph: number | null | undefined, theme: Theme) {
  if (oph == null) return theme === 'light' ? 'text-[#5f6368]' : 'text-gray-500';
  if (oph >= 30) return theme === 'light' ? 'text-green-700' : 'text-green-400';
  if (oph >= 20) return theme === 'light' ? 'text-amber-600' : 'text-yellow-400';
  return theme === 'light' ? 'text-red-600' : 'text-red-400';
}
export function deltaCls(sign: Delta['delta_sign'], theme: Theme) {
  if (sign === 'up') return theme === 'light' ? 'text-green-700' : 'text-green-400';
  if (sign === 'down') return theme === 'light' ? 'text-red-600' : 'text-red-400';
  return theme === 'light' ? 'text-[#8a8f96]' : 'text-gray-500';
}
export const deltaArrow = (sign: Delta['delta_sign']) => sign === 'up' ? '▲' : sign === 'down' ? '▼' : sign === 'flat' ? '▬' : '';
export const fmtDelta = (d: number | null | undefined) => d == null ? '' : `${d > 0 ? '+' : ''}${d.toFixed(1)}/h`;

const STAGE: Record<string, string> = {
  before_picking: 'not started', picking: 'picking', changeover: 'between picking and packing',
  packing: 'packing', done: 'finished',
};

/** Fetch the delta for a round (or for the round of an order). `person` = whose previous
 * round to compare with (a device passes the chosen picker/packer); a personal login
 * needs nothing. Polls every `pollMs`; `refreshKey` forces a fetch (after each stop / order). */
export function useDelta(src: { roundId?: number | null; orderId?: string | null }, person: string | null | undefined,
                         refreshKey: unknown, pollMs = 15000) {
  const [d, setD] = useState<Delta | null>(null);
  const url = src.roundId ? `${API}/pp/rounds/${src.roundId}/delta`
    : src.orderId ? `${API}/pp/orders/${encodeURIComponent(src.orderId)}/delta` : null;
  useEffect(() => {
    if (!url) return;
    let alive = true;
    const q = person ? `?person=${encodeURIComponent(person)}` : '';
    const load = () => authFetch(url + q).then(r => r.ok ? r.json() : null)
      .then(x => { if (alive && x) setD(x); }).catch(() => {});
    // now, and again once the screen's buffered events (flushed every 2 s) have landed —
    // otherwise the stop / order just finished is not in the answer until the next poll
    load();
    const again = window.setTimeout(load, 2500);
    const t = window.setInterval(load, pollMs);
    return () => { alive = false; window.clearTimeout(again); window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, person, refreshKey, pollMs]);
  return d;
}

/** The chip: "▲ 24.3/h". Renders nothing until the first answer with a round. */
export function SpeedChip({ d, theme, onClick }: { d: Delta | null; theme: Theme; onClick?: () => void }) {
  if (!d || !d.round_id) return null;
  const box = theme === 'light' ? 'bg-[#ffffff] border border-[#9aa0a6]' : 'bg-gray-800 border border-gray-700';
  return (
    <button type="button" onClick={onClick} onMouseDown={e => e.preventDefault()} title="Expected speed of this round — tap for the parts"
      className={`flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-full ${box}`}>
      {d.delta_sign && <span className={deltaCls(d.delta_sign, theme)}>{deltaArrow(d.delta_sign)}</span>}
      <span className={speedCls(d.expected_oph, theme)}>{d.expected_oph != null ? `${d.expected_oph.toFixed(1)}/h` : '—'}</span>
    </button>
  );
}

/** The sheet behind the chip: what is done, what is left, the previous round. */
export function SpeedSheet({ d, theme, onClose }: { d: Delta; theme: Theme; onClose: () => void }) {
  const light = theme === 'light';
  const panel = light ? 'bg-[#f3f4f6] border-[#c8ccd2] text-[#111111]' : 'bg-[#1a1d27] border-gray-800 text-white';
  const muted = light ? 'text-[#5f6368]' : 'text-gray-400';
  const row = light ? 'border-[#c8ccd2]' : 'border-gray-800';
  const ref = d.ref;
  const lines: [string, React.ReactNode][] = [];
  lines.push(['Round', `#${d.round_id} · ${d.orders ?? 0} orders · ${STAGE[d.stage || ''] || d.stage}`]);
  if (d.plan_stops != null)
    lines.push(['Picking', `${d.stops_done ?? 0} of ${d.plan_stops} stops${d.picking_min != null ? ` · ${fmtDur(d.picking_min)}${d.picking_final ? '' : ' so far'}` : ''}${d.sec_per_stop != null ? ` · ${d.sec_per_stop} s/stop` : ''}`]);
  else if (d.picking_min != null)
    lines.push(['Picking', `${fmtDur(d.picking_min)}${d.picking_final ? '' : ' so far'}`]);
  if (d.plan_metres != null)
    lines.push(['Walk', `${Math.round(d.plan_metres)} m on the floor plan${d.metres_left != null && d.stage !== 'done' ? ` · ${Math.round(d.metres_left)} m left` : ''}${d.metres_scale != null && Math.abs(d.metres_scale - 1) >= 0.1 ? ` · stops ${d.metres_scale > 1 ? 'further apart' : 'closer'} than last time (×${d.metres_scale})` : ''}`]);
  if (d.pace_parts && d.stage === 'picking')
    lines.push(['Still to pick', `${fmtDur(d.pace_parts.walk_min)} walking · ${fmtDur(d.pace_parts.shelf_min)} at the shelves (${d.pace_parts.pieces_left} pcs)${d.pace_parts.box_min ? ` · ${fmtDur(d.pace_parts.box_min)} box changes` : ''} — ${d.pace_source === 'own' ? 'your pace' : 'warehouse pace'}`]);
  if (d.swaps_planned || d.swaps_done)
    lines.push(['Box swaps', `${d.swaps_done ?? 0}${d.swaps_planned != null ? ` of ${d.swaps_planned}` : ''} done${d.swap_min != null ? ` · ${fmtDur(d.swap_min)}` : ''}${ref?.sec_per_swap != null ? ` · last time ${Math.round(ref.sec_per_swap)} s each` : ''}`]);
  if (d.changeover_min != null) lines.push(['Picking → packing', fmtDur(d.changeover_min)]);
  if (d.packing_min != null)
    lines.push(['Packing', `${d.packed ?? 0} of ${d.live ?? 0} orders · ${fmtDur(d.packing_min)}${d.packing_final ? '' : ' so far'}${d.sec_per_order != null ? ` · ${d.sec_per_order} s/order` : ''}`]);
  else lines.push(['Packing', `${d.live ?? 0} orders to pack`]);
  lines.push(['Elapsed', `${fmtDur(d.elapsed_min ?? 0)}${d.stage !== 'done' && d.remaining_min != null ? ` · about ${fmtDur(d.remaining_min)} left at your last pace` : ''}`]);
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className={`w-full sm:max-w-md border rounded-t-2xl sm:rounded-2xl p-5 ${panel}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-3">
          <div className="font-bold text-lg">{d.stage === 'done' ? 'Round speed' : 'Expected round speed'}</div>
          <button onClick={onClose} className={`text-sm ${muted}`}>✕ Close</button>
        </div>
        <div className="flex items-baseline gap-3 mb-1">
          <span className={`text-5xl font-black ${speedCls(d.expected_oph, theme)}`}>{d.expected_oph != null ? d.expected_oph.toFixed(1) : '—'}</span>
          <span className={muted}>orders / hour</span>
        </div>
        <div className={`mb-4 font-semibold ${deltaCls(d.delta_sign, theme)}`}>
          {ref && d.delta != null
            ? `${deltaArrow(d.delta_sign)} ${fmtDelta(d.delta)} vs your last round (${ref.oph.toFixed(1)}/h on ${ref.date}, ${ref.orders} orders)`
            : ref ? `Last round: ${ref.oph.toFixed(1)}/h on ${ref.date}` : 'No previous round to compare with yet'}
        </div>
        <div className="text-sm">
          {lines.map(([k, v]) => (
            <div key={k} className={`flex justify-between gap-4 py-1.5 border-t ${row}`}>
              <span className={muted}>{k}</span><span className="text-right">{v}</span>
            </div>
          ))}
          {ref && (
            <div className={`flex justify-between gap-4 py-1.5 border-t ${row}`}>
              <span className={muted}>Last round pace</span>
              <span className="text-right">{ref.sec_per_stop} s/stop · {ref.sec_per_order} s/order</span>
            </div>
          )}
        </div>
        {d.stage !== 'done' && (
          <p className={`text-xs mt-3 ${muted}`}>
            What is done counts at your real pace; what is left counts at your last round's pace. The number moves as you work.
          </p>
        )}
      </div>
    </div>
  );
}

/** Chip + sheet in one, for a header slot. */
export default function SpeedChipLive({ src, person, theme, refreshKey }:
  { src: { roundId?: number | null; orderId?: string | null }; person?: string | null; theme: Theme; refreshKey?: unknown }) {
  const d = useDelta(src, person, refreshKey);
  const [open, setOpen] = useState(false);
  return (
    <>
      <SpeedChip d={d} theme={theme} onClick={() => setOpen(true)} />
      {open && d && <SpeedSheet d={d} theme={theme} onClose={() => setOpen(false)} />}
    </>
  );
}
